import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { FloorPlan, LocationNode } from '@oscom/shared';
import { getCustomer } from '../api/customers';
import { floorPlanPreviewUrl, listFloorPlans } from '../api/floor-plans';
import { listLocations } from '../api/locations';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { FloorPlanUploadDialog } from '../components/FloorPlanUploadDialog';
import { Layout } from '../components/Layout';
import { locationPath } from '../plans/locationPath';
import { NotFoundPage } from './NotFoundPage';

/**
 * Floor plans of one customer. Reading needs `customers:read`, uploading
 * `customers:write` and `assets:write`. The page only exists while the
 * `floorPlans` switch is on (read defensively: deploy skew -> "not found").
 */
export function FloorPlansPage(): ReactNode {
  const { session } = useSession();
  const navigate = useNavigate();
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();
  const enabled = session?.status === 'authenticated' && session.me.features?.floorPlans === true;

  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [plans, setPlans] = useState<FloorPlan[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!customerIdParam || !enabled) return;
    let cancelled = false;
    Promise.all([
      getCustomer(customerIdParam),
      listLocations(customerIdParam),
      listFloorPlans(customerIdParam),
    ])
      .then(([customer, locationResponse, planResponse]) => {
        if (cancelled) return;
        setCustomerName(customer.name);
        setLocations(locationResponse.locations);
        setPlans(planResponse.plans);
        setLoadError(undefined);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setLoadError(
          caught instanceof Error ? caught.message : 'Pläne konnten nicht geladen werden.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [customerIdParam, enabled]);

  if (session?.status !== 'authenticated') return null;
  if (!enabled) return <NotFoundPage />;
  if (!customerIdParam) return null;
  const { me } = session;
  const customerId = customerIdParam;
  const canWrite =
    me.permissions.includes('customers:write') && me.permissions.includes('assets:write');
  const base = `/kunden/${encodeURIComponent(customerId)}/plaene`;
  const locationsById = new Map(locations.map((node) => [node.id, node]));

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <CustomerContextNav
            customerId={customerId}
            customerName={customerName}
            features={me.features}
          />
        ) : null}

        <div className="card">
          <h1>Pläne</h1>
          {canWrite && !uploading ? (
            <p>
              <button
                type="button"
                className="button button--primary"
                onClick={() => setUploading(true)}
              >
                Plan hochladen
              </button>
            </p>
          ) : null}

          {loadError ? (
            <p className="notice notice--warning">{loadError}</p>
          ) : plans === undefined ? (
            <p className="muted" role="status" aria-live="polite">
              Pläne werden geladen ...
            </p>
          ) : plans.length === 0 ? (
            <p className="muted">Noch kein Plan hochgeladen.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Vorschau</th>
                    <th scope="col">Name</th>
                    <th scope="col">Ort</th>
                    <th scope="col">Bildgröße</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => (
                    <tr key={plan.id}>
                      <td>
                        <img
                          loading="lazy"
                          className="plan-thumb"
                          alt={plan.name}
                          src={floorPlanPreviewUrl(customerId, plan)}
                        />
                      </td>
                      <td>
                        <Link to={`${base}/${encodeURIComponent(plan.id)}`}>{plan.name}</Link>
                      </td>
                      <td>{locationPath(plan.locationId, locationsById)}</td>
                      <td>
                        {plan.currentImage.width} × {plan.currentImage.height} px
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {canWrite && uploading ? (
          <FloorPlanUploadDialog
            customerId={customerId}
            locations={locations}
            onCancel={() => setUploading(false)}
            onDone={(plan) => navigate(`${base}/${encodeURIComponent(plan.id)}`)}
          />
        ) : null}
      </section>
    </Layout>
  );
}
